/**
 * SSO Provider (singleton)
 *
 * Wraps the corporate identity provider behind generic identifiers.
 * Do not leak the underlying technology in variable names, storage keys,
 * logs or error messages (the npm import line below is fine — bundlers
 * mangle it).
 */

import Keycloak from 'keycloak-js';
import { SSO_CONFIG } from '@/config/constants';

const ssoProvider = new Keycloak({
  url: SSO_CONFIG.URL,
  realm: SSO_CONFIG.REALM,
  clientId: SSO_CONFIG.CLIENT_ID,
});

let initPromise: Promise<boolean> | null = null;

/**
 * Initialize the SSO session check. Silent — never shows a login form.
 * Safe to call multiple times; the check runs only once.
 */
export const initSso = (): Promise<boolean> => {
  if (!initPromise) {
    initPromise = ssoProvider.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      pkceMethod: 'S256',
    });
  }
  return initPromise;
};

/**
 * Redirect the current tab to the corporate portal (the single entry
 * point for all apps). Never use window.open() — browsers block it.
 */
export const redirectToPortal = (): void => {
  window.location.href = `${SSO_CONFIG.PORTAL_URL}?app=${SSO_CONFIG.CLIENT_ID}`;
};

export const isAuthenticated = (): boolean => ssoProvider.authenticated === true;

/**
 * Return a fresh access token, refreshing it silently if it expires in
 * less than 30 seconds. Returns null when there is no valid session.
 */
export const getSsoToken = async (): Promise<string | null> => {
  if (!ssoProvider.authenticated) {
    return null;
  }
  try {
    await ssoProvider.updateToken(30);
  } catch {
    return null;
  }
  return ssoProvider.token ?? null;
};

/**
 * Roles assigned to the user for this app (client roles).
 */
export const getAppRoles = (): string[] =>
  ssoProvider.tokenParsed?.resource_access?.[SSO_CONFIG.CLIENT_ID]?.roles ?? [];

export interface SsoIdentity {
  id: string;
  fullName: string;
  email: string;
}

export const getSsoIdentity = (): SsoIdentity | null => {
  const parsed = ssoProvider.tokenParsed;
  if (!parsed?.sub) {
    return null;
  }
  return {
    id: parsed.sub,
    fullName: (parsed['name'] as string | undefined) ?? '',
    email: (parsed['email'] as string | undefined) ?? '',
  };
};

/**
 * End the SSO session and land on the portal.
 */
export const ssoLogout = (): void => {
  void ssoProvider.logout({ redirectUri: SSO_CONFIG.PORTAL_URL });
};
